export const services = [
  {
    id: 1,
    icon: "Palette",
    title: "Brand Identity",
    description: "Craft distinctive brand identities that resonate with your audience and stand the test of time.",
    details: "Logo design, brand guidelines, visual systems, and strategic positioning that tells your unique story."
  },
  {
    id: 2,
    icon: "Monitor",
    title: "Web Design",
    description: "Design immersive digital experiences that captivate users and drive meaningful engagement.",
    details: "Responsive websites, landing pages, UI/UX design, and interactive prototypes built for conversion."
  },
  {
    id: 3,
    icon: "FileText",
    title: "Print Design",
    description: "Create stunning print materials that leave a lasting impression in the physical world.",
    details: "Business cards, brochures, posters, packaging inserts, and editorial layouts with meticulous craft."
  },
  {
    id: 4,
    icon: "Box",
    title: "Packaging Design",
    description: "Design packaging that tells your product's story and commands shelf presence.",
    details: "Product packaging, label design, unboxing experiences, and sustainable packaging solutions."
  },
  {
    id: 5,
    icon: "Play",
    title: "Motion Graphics",
    description: "Bring your brand to life with dynamic animations and compelling visual storytelling.",
    details: "Logo animations, explainer videos, social media content, and presentation animations."
  },
  {
    id: 6,
    icon: "PenTool",
    title: "Illustration",
    description: "Custom illustrations that add personality and depth to your brand communications.",
    details: "Digital illustration, icon systems, infographics, editorial illustrations, and character design."
  }
];

export const portfolioProjects = [
  {
    id: 1,
    title: "Lumière Cosmetics",
    category: "Brand Identity",
    description: "A complete brand overhaul for a luxury cosmetics company, featuring a refined visual identity that speaks to modern elegance.",
    image: "https://images.unsplash.com/photo-1619209629065-e9a2b225b24b?w=800&h=600&fit=crop",
    tags: ["Branding", "Visual Identity", "Packaging"],
    year: "2025"
  },
  {
    id: 2,
    title: "TerraVerde Architecture",
    category: "Web Design",
    description: "An immersive website experience for a sustainable architecture firm, showcasing their portfolio through dynamic scroll interactions.",
    image: "https://images.unsplash.com/photo-1652805363265-b8fbf9bbdfac?w=800&h=600&fit=crop",
    tags: ["Web Design", "UI/UX", "Development"],
    year: "2025"
  },
  {
    id: 3,
    title: "Artisan Coffee Co.",
    category: "Packaging Design",
    description: "Artisanal packaging design for a specialty coffee brand, blending rustic charm with contemporary minimalism.",
    image: "https://images.unsplash.com/photo-1683818051102-dd1199d163b9?w=800&h=600&fit=crop",
    tags: ["Packaging", "Print", "Branding"],
    year: "2024"
  },
  {
    id: 4,
    title: "Nexus Tech Summit",
    category: "Print Design",
    description: "A comprehensive event identity system including posters, badges, wayfinding, and digital assets for a global tech conference.",
    image: "https://images.unsplash.com/photo-1490013616775-3ca8865fb129?w=800&h=600&fit=crop",
    tags: ["Print", "Event Design", "Visual System"],
    year: "2024"
  },
  {
    id: 5,
    title: "Flora & Fauna",
    category: "Illustration",
    description: "A series of botanical illustrations for an eco-luxury lifestyle brand, used across packaging, editorial, and digital platforms.",
    image: "https://images.unsplash.com/photo-1636247499734-893da2bcfc1c?w=800&h=600&fit=crop",
    tags: ["Illustration", "Editorial", "Packaging"],
    year: "2024"
  },
  {
    id: 6,
    title: "Velocity Sportswear",
    category: "Brand Identity",
    description: "Dynamic brand identity for an athletic wear startup, featuring bold typography and an energetic color system.",
    image: "https://images.unsplash.com/photo-1730206562928-0efd62560435?w=800&h=600&fit=crop",
    tags: ["Branding", "Visual Identity", "Motion"],
    year: "2025"
  }
];

export const featuredCaseStudies = [
  {
    id: 1,
    title: "Brand Identity",
    client: "Lumière Cosmetics",
    thumbnail: "https://images.unsplash.com/photo-1619209629065-e9a2b225b24b?w=600&h=400&fit=crop",
    description: "Craft distinctive brand identities that resonate with your audience and stand out in a competitive market.",
    link: "/portfolio"
  },
  {
    id: 2,
    title: "Digital Experience",
    client: "TerraVerde Architecture",
    thumbnail: "https://images.unsplash.com/photo-1652805363265-b8fbf9bbdfac?w=600&h=400&fit=crop",
    description: "Build immersive digital experiences with purposeful design that drives meaningful user engagement and conversions.",
    link: "/portfolio"
  },
  {
    id: 3,
    title: "Packaging System",
    client: "Artisan Coffee Co.",
    thumbnail: "https://images.unsplash.com/photo-1683818051102-dd1199d163b9?w=600&h=400&fit=crop",
    description: "Design comprehensive packaging systems that elevate product presence and create memorable unboxing experiences.",
    link: "/portfolio"
  }
];

export const testimonials = [
  {
    id: 1,
    quote: "Grapix transformed our brand from forgettable to unforgettable. Their strategic approach to design helped us stand out in a crowded market.",
    author: "Sarah Chen",
    role: "CEO, Lumière Cosmetics",
    avatar: "SC"
  },
  {
    id: 2,
    quote: "The attention to detail and creative vision Grapix brought to our project exceeded every expectation. They truly understand the power of great design.",
    author: "Marcus Rivera",
    role: "Founder, TerraVerde Architecture",
    avatar: "MR"
  },
  {
    id: 3,
    quote: "Working with Grapix was effortless. They took our vague ideas and turned them into a cohesive brand experience that our customers love.",
    author: "Emily Nakamura",
    role: "Creative Director, Artisan Coffee Co.",
    avatar: "EN"
  }
];

export const blogPosts = [
  {
    id: 1,
    title: "The Future of Brand Design in 2025",
    excerpt: "Exploring emerging trends in brand identity design and how studios are adapting to the new creative landscape.",
    image: "https://images.unsplash.com/photo-1742440710226-450e3b85c100?w=1200&h=800&fit=crop",
    author: "Alex Morgan",
    authorImage: "AM",
    date: "Jan 15, 2025",
    category: "Design Trends",
    readTime: "5 min read",
    content: [
      { type: "paragraph", text: "As we move further into 2025, the landscape of brand design is shifting beneath our feet. The days of static, monolithic brand identities are fading, replaced by living, breathing design systems that adapt to every user interaction." },
      { type: "heading", text: "The Rise of Kinetic Identity" },
      { type: "paragraph", text: "Motion is no longer an afterthought; it is the core of modern branding. Kinetic identities—brands that move, shift, and respond—are becoming the standard. This isn't just about animation; it's about behavior. How does a logo react when you hover over it? How does a typeface expand when you scroll?" },
      { type: "image", src: "https://images.unsplash.com/photo-1550745165-9bc0b252726f?w=1200&h=800&fit=crop", caption: "Dynamic typography in motion." },
      { type: "paragraph", text: "We are seeing a move away from rigid guidelines toward 'brand behaviors'—rules that define how a brand acts rather than just how it looks. This allows for infinite variation while maintaining a cohesive soul." },
      { type: "heading", text: "Sustainability as a Visual Language" },
      { type: "paragraph", text: "Sustainability is influencing aesthetics directly. Brands are opting for lighter digital footprints, using darker modes by default to save energy on OLED screens, and choosing typefaces that require less ink when printed. The aesthetic of efficiency is becoming a premium signifier." }
    ]
  },
  {
    id: 2,
    title: "Minimalism Meets Maximalism",
    excerpt: "How the best brands are finding the perfect balance between clean simplicity and bold visual expression.",
    image: "https://images.unsplash.com/photo-1742440710193-3547e0b9d4db?w=1200&h=800&fit=crop",
    author: "Jordan Lee",
    authorImage: "JL",
    date: "Dec 28, 2024",
    category: "Branding",
    readTime: "4 min read",
    content: [
      { type: "paragraph", text: "For years, the design pendulum swung between extreme minimalism (think 'blanding') and chaotic maximalism. Now, we are finding a beautiful middle ground. We call it 'Expressive Utility'." },
      { type: "heading", text: "The End of 'Blanding'" },
      { type: "paragraph", text: "The era of sans-serif, geometric, friendly startup logos is waning. Brands are rediscovering the power of idiosyncrasy. They are embracing serif fonts with character, custom illustrations, and unexpected color palettes." },
      { type: "quote", text: "True minimalism isn't about having less; it's about making every element count for more." },
      { type: "paragraph", text: "This shift allows functionality to remain king while personality takes the throne. It is about being clear, not boring. Bold, not messy." }
    ]
  },
  {
    id: 3,
    title: "Designing for Sustainability",
    excerpt: "Practical approaches to creating environmentally conscious packaging and print designs without compromising aesthetics.",
    image: "https://images.unsplash.com/photo-1648111144138-023d24d16a95?w=1200&h=800&fit=crop",
    author: "Priya Patel",
    authorImage: "PP",
    date: "Dec 10, 2024",
    category: "Sustainability",
    readTime: "6 min read",
    content: [
      { type: "paragraph", text: "Sustainable design is often associated with earthy tones and rough textures. But the new wave of eco-conscious design is sleek, modern, and vibrant. It proves that you don't have to sacrifice luxury for responsibility." },
      { type: "heading", text: "Material Innovation" },
      { type: "paragraph", text: "Mushroom packaging, algae ink, and recycled ocean plastics are moving from prototypes to mass production. The challenge for designers is to tell the story of these materials without falling into greenwashing tropes." },
      { type: "image", src: "https://images.unsplash.com/photo-1605647540924-852290f6b0d5?w=1200&h=800&fit=crop", caption: "Biodegradable packaging solutions." },
      { type: "paragraph", text: "We are entering an age where the lifespan of a design is as important as its immediate impact. Designers are asking: 'What happens to this box after it's opened?' and designing for that second life." }
    ]
  },
  {
    id: 4,
    title: "Typography as Brand Architecture",
    excerpt: "How typeface selection and typographic hierarchy can define your entire brand personality and customer perception.",
    image: "https://images.unsplash.com/photo-1765383563504-63f95cab942c?w=1200&h=800&fit=crop",
    author: "Jordan Lee",
    authorImage: "JL",
    date: "Nov 22, 2024",
    category: "Typography",
    readTime: "7 min read",
    content: [
      { type: "paragraph", text: "Type is voice. It is the clothes your words wear. In a digital-first world, where photography can be stock and layouts responsive, typography is often the most consistent brand asset." },
      { type: "heading", text: "Custom Typefaces" },
      { type: "paragraph", text: "More brands are commissioning custom typefaces not just for ownership, but for performance. A variable font can adjust its weight and width based on screen size or user context, serving both utility and expression." }
    ]
  },
  {
    id: 5,
    title: "Behind the Process: A Brand Redesign",
    excerpt: "An inside look at our workflow when tackling a complete brand overhaul, from discovery to final delivery.",
    image: "https://images.unsplash.com/photo-1730206562928-0efd62560435?w=1200&h=800&fit=crop",
    author: "Alex Morgan",
    authorImage: "AM",
    date: "Nov 5, 2024",
    category: "Process",
    readTime: "8 min read",
    content: [
      { type: "paragraph", text: "Redesigning a brand is like renovating a house while someone is still living in it. You have to respect the foundation while modernizing the experience." },
      { type: "heading", text: "The Discovery Phase" },
      { type: "paragraph", text: "We start by listening. Not just to the founders, but to the customers. The gap between how a brand sees itself and how the world sees it is where the design opportunity lies." }
    ]
  }
];

export const teamMembers = [
  {
    id: 1,
    name: "Alex Morgan",
    role: "Creative Director & Founder",
    bio: "With 15+ years in brand design, Alex leads Grapix's creative vision. Previously at Pentagram and IDEO.",
    avatar: "AM",
    videoUrl: "https://youtu.be/yiHKlPuZ73c"
  },
  {
    id: 2,
    name: "Jordan Lee",
    role: "Lead Designer",
    bio: "Specializing in visual identity systems, Jordan crafts brand experiences that are both timeless and contemporary.",
    avatar: "JL",
    videoUrl: "https://www.youtube.com/watch?v=q2fIWB8o-bs"
  },
  {
    id: 3,
    name: "Priya Patel",
    role: "UX/UI Designer",
    bio: "Priya bridges the gap between beautiful design and intuitive user experience across digital platforms.",
    avatar: "PP",
    videoUrl: "https://www.youtube.com/watch?v=eEzD-Y97ges"
  },
  {
    id: 4,
    name: "Sam Torres",
    role: "Motion Designer",
    bio: "Sam brings brands to life through animation, creating dynamic visual narratives that captivate audiences.",
    avatar: "ST",
    videoUrl: "https://www.youtube.com/watch?v=8MqqJ2jR09A"
  }
];

export const studioStats = [
  { label: "Projects Delivered", value: "200+" },
  { label: "Happy Clients", value: "85+" },
  { label: "Design Awards", value: "30+" },
  { label: "Years of Craft", value: "12+" }
];

export const consultPackages = [
  {
    id: 1,
    title: "Discovery Call",
    price: "Free",
    duration: "30 min",
    description: "A no-commitment introduction to explore your project needs and how Grapix can help.",
    features: [
      "Project scope discussion",
      "Budget alignment",
      "Timeline overview",
      "Team introduction"
    ],
    cta: "Book Free Call",
    highlighted: false
  },
  {
    id: 2,
    title: "Strategy Session",
    price: "$250",
    duration: "90 min",
    description: "Deep-dive into your brand's challenges with actionable recommendations and a strategic roadmap.",
    features: [
      "Brand audit & analysis",
      "Competitive landscape review",
      "Strategic recommendations",
      "Action plan document",
      "Follow-up support"
    ],
    cta: "Book Session",
    highlighted: true
  },
  {
    id: 3,
    title: "Full Engagement",
    price: "Custom",
    duration: "Ongoing",
    description: "End-to-end design partnership tailored to your brand's unique goals and aspirations.",
    features: [
      "Dedicated design team",
      "Comprehensive brand strategy",
      "Multi-deliverable execution",
      "Iterative refinement",
      "Priority support"
    ],
    cta: "Get in Touch",
    highlighted: false
  }
];

export const floatingIcons = [
  "Palette", "PenTool", "Sparkles", "Layers", "Type",
  "Droplets", "Figma", "Image", "Shapes", "Grid3X3",
  "Brush", "Scissors", "Aperture", "Lightbulb", "Gem",
  "Wand2", "Layout", "Stamp", "CircleDot", "Star",
  "Hexagon", "Triangle"
];
